'use client';

import { useEffect, useState } from 'react';
import { apiClient } from '@/lib/api';
import { getAccessToken } from '@/lib/authStorage';

type AdminAccess = Awaited<ReturnType<typeof apiClient.checkAdminAccess>>;
type AccessState = {
  isAuthed: boolean;
  hydrated: boolean;
  checked: boolean;
  access: AdminAccess | null;
};

export function useAdminAccess(enabled = true) {
  const [state, setState] = useState<AccessState>({ isAuthed: false, hydrated: false, checked: false, access: null });

  useEffect(() => {
    let alive = true;
    let version = 0;
    const refresh = () => {
      const token = getAccessToken();
      const requestVersion = ++version;
      setState({ isAuthed: Boolean(token), hydrated: true, checked: !token, access: null });
      if (!token || !enabled) return;

      const apply = (access: AdminAccess | null) => {
        if (!alive || version !== requestVersion) return;
        // API может обновить токен без события; проверяем новую сессию вместо принятия старого ответа.
        if (getAccessToken() !== token) {
          refresh();
          return;
        }
        setState({ isAuthed: true, hydrated: true, checked: true, access });
      };
      void apiClient.checkAdminAccess().then(apply).catch(() => apply(null));
    };
    const onStorage = (event: StorageEvent) => {
      if (!event.key || ['access', 'user', 'refresh'].includes(event.key)) refresh();
    };

    refresh();
    window.addEventListener('lisad_auth_changed', refresh);
    window.addEventListener('storage', onStorage);
    return () => {
      alive = false;
      version++;
      window.removeEventListener('lisad_auth_changed', refresh);
      window.removeEventListener('storage', onStorage);
    };
  }, [enabled]);

  const hasAdminAccess = Boolean(state.access?.has_access);
  const canViewHomePreviews = hasAdminAccess && ['moderator', 'admin', 'superuser'].includes(state.access?.admin?.scope || '');
  return { isAuthed: state.isAuthed, hydrated: state.hydrated, checked: state.checked, hasAdminAccess, canViewHomePreviews };
}
